import { Box, Grid, Typography } from "@mui/material";
import pic from "assets/splashpage/launchpad_splashpage.webp";
import testimonial1 from "assets/splashpage/testimonial1.webp";
import testimonial2 from "assets/splashpage/testimonial2.webp";
import testimonial3 from "assets/splashpage/testimonial3.webp";
import { SplashPage, SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect } from "react";
import {
  Route,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { SearchParams } from "schemas/dashboard";
import { Plans } from "schemas/payments";
import { routes } from "schemas/routes";

import { PAGE_VISITED } from "constants/trackingProps";
import { getSource, trackEvent } from "utils/tracking";
import { useSubscription } from "utils/useSubscription";

import SplashPageHome from "../components/Home";
import LaunchpadIntakeForm from "./components/IntakeForm";
import LaunchpadSuccessPage from "./components/SuccessPage";

const LaunchpadSplashPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const source = getSource();
  const { subscription, isConciergePlan } = useContext(SubscriptionContext);
  const alreadyPaid = subscription?.isSubscribed && isConciergePlan;
  const { handleCheckout } = useSubscription();

  const [searchParams] = useSearchParams();
  const conciergeOption = searchParams.get(SearchParams.CONCIERGE_OPTION);

  const checkOutForSplashPage = (plan: Plans) => {
    if (alreadyPaid) {
      navigate(`/${routes.launchpadIntakeForm}${location.search}`, {
        replace: true,
      });
    } else {
      handleCheckout(
        Plans.CONCIERGE_YEARLY,
        source || "",
        window.location.pathname,
        SplashPage.launchpad,
      );
    }
  };

  useEffect(() => {
    trackEvent(PAGE_VISITED, {
      source: getSource(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SplashPageHome
      checkOutForSplashPage={checkOutForSplashPage}
      sidebarPic={pic}
      planDetails={
        <>
          <Typography variant="h2">Bento Launchpad</Typography>
          <Typography variant="h6" gutterBottom>
            Your all-in-one creator growth system.
          </Typography>

          <Typography gutterBottom>
            Built for early creators ready to land their first paid brand deals
            — with the structure, tools, and support to make it simple.
          </Typography>

          <Box sx={{ mt: 6 }}>
            <Typography sx={{ mb: 2 }}>
              <strong>What's Inside</strong>
            </Typography>

            <Typography sx={{ mb: 2, ml: 2 }}>
              💡 <strong>Step By Step Personalized Roadmap:</strong> <br />
              Get a plan built around your creator goals — with clear,
              actionable steps to help you grow, pitch, and land your first
              collaborations. We'll show you exactly what to focus on next —
              from refining your niche to building your portfolio — so you can
              build real momentum and see steady progress.
            </Typography>

            <Typography sx={{ mb: 2, ml: 2 }}>
              👥 <strong>Monthly Group Coaching & Community:</strong> <br />
              Join a small group of early creators and learn directly from an
              experienced coach. Get feedback, accountability, and advice every
              month — while connecting with creators at the same stage as you.
            </Typography>

            <Typography sx={{ mb: 2, ml: 2 }}>
              💬 <strong>Brand Deal Helper:</strong> <br />
              Never wonder what to say in a brand conversation again. From media
              kit prep to rate negotiation, our insights (from thousands of real
              brand deals) help you respond confidently and close better
              partnerships.
            </Typography>

            <Typography sx={{ mb: 2, ml: 2 }}>
              📥{" "}
              <strong>
                300 Pitch Emails Sent For You Each Month (VA Plan Included):{" "}
              </strong>
              <br />
              Think of this as your creator assistant. We handle outreach —
              crafting and sending brand pitches that match your goals — so you
              can focus on creating while we keep your inbox and deal pipeline
              active.
            </Typography>
          </Box>

          <Grid
            container
            justifyContent={"center"}
            alignItems="center"
            spacing={1}
          >
            <Box component="img" src={testimonial1} sx={{ height: 100 }} />
            <Box component="img" src={testimonial2} sx={{ height: 100 }} />
            <Box component="img" src={testimonial3} sx={{ height: 100 }} />
          </Grid>
        </>
      }
      buttonText={
        <>
          Starting at{" "}
          <strong>
            ${Number(conciergeOption) === 0 ? "99.00" : "66.67"}/mo
          </strong>
          . Flexible monthly plan. Cancel anytime.
          <br />
        </>
      }
      fromRoutes={`${routes.launchpad}`}
      plan={Plans.CONCIERGE_YEARLY}
      notQualified={Boolean(alreadyPaid)}
      buttonLabel="Join"
      additionalRoutes={
        <>
          <Route path="/intake-form/*" element={<LaunchpadIntakeForm />} />
          <Route path="/success" element={<LaunchpadSuccessPage />} />
        </>
      }
    />
  );
};

export default LaunchpadSplashPage;
